import React from "react";
import { Box } from "@mui/material";
import moment from "moment-timezone";
import { FieldGrid, InfoCard, SectionTitle } from "./DataDisplay";

const LABEL_SX = { fontSize: "14px" };

const PARAMETERS_CARD_SX = {
  backgroundColor: "var(--app-surface)",
  border: "1px solid var(--app-border)",
  p: "15px",
  cursor: "default",
};

const formatStatus = (status) =>
  status
    ?.toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatDate = (value) =>
  value ? moment(value).format("DD-MM-YYYY, hh:mm A") : "--";

const SECTIONS = {
  interpretation: {
    title: "Interpretation Details",
    isVisible: (data) =>
      data?.interpretation_request_status === "completed" ||
      data?.interpretation_request_status === "in progress",
    isLoading: (data) => !data,
    containerProps: { spacing: 2, justifyContent: "space-between" },
    itemProps: { xs: 12, sm: 6, md: 2.4 },
    fieldProps: { stacked: true, skeleton: "wave", labelSx: LABEL_SX },
    buildItems: (data) => [
      {
        label: "STATUS",
        value: formatStatus(data?.interpretation_request_status),
      },
      { label: "REQUESTED BY", value: data?.requested_by || "Auto Requested" },
      {
        label: "REQUESTED AT",
        value: formatDate(data?.interpretation_display_requested_at),
      },
      {
        label: "COMPLETED AT",
        value: formatDate(data?.interpretation_display_completed_at),
      },
      {
        label: "INTERPRETED BY",
        value: data?.interpreter_details?.signature_name || "--",
      },
    ],
  },
  parameters: {
    title: "ECG Parameters",
    containerProps: { spacing: 6 },
    itemProps: { xs: 6, sm: "auto" },
    fieldProps: { labelSx: LABEL_SX },
    buildItems: (data) => {
      const ecg = data?.characteristics;

      return [
        { label: "HEART RATE", value: `${ecg?.bpm ?? "--"} bpm` },
        { label: "PR INTERVAL", value: `${ecg?.pr ?? "--"} ms` },
        { label: "QRS DURATION", value: `${ecg?.qrs ?? "--"} ms` },
        { label: "QT INTERVAL", value: `${ecg?.qt ?? "--"} ms` },
        { label: "QTc INTERVAL", value: `${ecg?.qtc ?? "--"} ms` },
        { label: "RR INTERVAL", value: `${ecg?.rr ?? "--"} ms` },
        { label: "ATRIAL RATE", value: `${ecg?.atrialRate ?? "--"} bpm` },
        {
          label: "VENTRICULAR RATE",
          value: `${ecg?.ventricularRate ?? "--"} bpm`,
        },
        {
          label: "P-R-T AXIS",
          value: `${ecg?.PAxisDegrees ?? "--"}° - ${
            ecg?.QRSAxisDegrees ?? "--"
          }° - ${ecg?.TAxisDegrees ?? "--"}°`,
        },
      ];
    },
  },
};

const ReportDetailsCard = ({ type = "parameters", data, loading = false }) => {
  const section = SECTIONS[type] || SECTIONS.parameters;
  const isInterpretation = type === "interpretation";

  if (section.isVisible && !section.isVisible(data)) {
    return <Box sx={{ width: "100%" }} />;
  }

  const grid = (
    <FieldGrid
      items={section.buildItems(data)}
      loading={section.isLoading ? section.isLoading(data) : loading}
      containerProps={section.containerProps}
      itemProps={section.itemProps}
      fieldProps={section.fieldProps}
    />
  );

  if (isInterpretation) {
    return (
      <Box sx={{ width: "100%" }}>
        <Box mb={2} mt={2}>
          <SectionTitle paddingBottom={1}>
            {section.title}
          </SectionTitle>

          <InfoCard variant="plain">{grid}</InfoCard>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <SectionTitle mb={1} sx={{ marginTop: "20px" }}>
        {section.title}
      </SectionTitle>

      <Box className="card" sx={PARAMETERS_CARD_SX}>
        {grid}
      </Box>
    </>
  );
};

export default ReportDetailsCard;
